
import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import {
  HiOutlineBars3,
  HiOutlineXMark,
  HiOutlineArrowRightOnRectangle,
  HiOutlineSquares2X2,
  HiOutlineUserCircle,
} from "react-icons/hi2";

import { logo, darkLogo } from "../../assets";
import { useStateContext } from "../../context/ContextProvider";

import ToggleLanguages from "../utils/ToggleLanguages";
import ToggleDark from "../utils/ToggleDark";

import Navebar from "./Navebar";
import Navmobile from "./Navmobile";

function Header({ active, DropActive }) {
  const { pathname } = useLocation();
  const { i18n, t } = useTranslation();
  const { currentMode } = useStateContext();

  const [menuOpen, setMenuOpen] = useState(false);
  const [logoError, setLogoError] = useState(false);

  const { isAuth, isAdmin } = useSelector(
    (state) => state.Users || {}
  );

  const language = (
    i18n.resolvedLanguage ||
    i18n.language ||
    "ar"
  )
    .toLowerCase()
    .startsWith("en")
    ? "en"
    : "ar";

  const isArabic = language === "ar";

  // User destination
  const accountPath = !isAuth
    ? "/Login"
    : isAdmin
    ? "/admin"
    : "/userdashboard";

  const accountLabel = !isAuth
    ? t("Login")
    : isAdmin
    ? t("Dashboard")
    : t("Profile");

  const AccountIcon = !isAuth
    ? HiOutlineArrowRightOnRectangle
    : isAdmin
    ? HiOutlineSquares2X2
    : HiOutlineUserCircle;

  // Close the menu when navigating
  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  // Recheck logo when theme changes
  useEffect(() => {
    setLogoError(false);
  }, [currentMode]);

  // Escape key and page scroll lock
  useEffect(() => {
    if (!menuOpen) return;

    const oldOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
      }
    };

    window.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = oldOverflow;
      window.removeEventListener(
        "keydown",
        handleEscape
      );
    };
  }, [menuOpen]);

  return (
    <>
      {/* ======================================
          FIXED GLASS HEADER
      ====================================== */}

      <header
        className="
          fixed inset-x-0 top-0 z-[110]
          w-full
          border-b border-white/45
          bg-white/75
          shadow-[0_6px_30px_rgba(10,28,45,0.06)]
          backdrop-blur-2xl
          backdrop-saturate-150
          transition-colors duration-300

          dark:border-white/10
          dark:bg-[#0B1726]/75
          dark:shadow-[0_8px_30px_rgba(0,0,0,0.18)]
        "
      >
        <div
          className="
            mx-auto grid h-[76px]
            w-full max-w-[1600px]
            grid-cols-[auto_minmax(0,1fr)_auto]
            items-center
            gap-3
            px-3
            sm:px-6
            lg:px-8
            xl:gap-6
          "
        >
          {/* ==================================
              LOGO
          ================================== */}

          <Link
            to="/"
            onClick={() => setMenuOpen(false)}
            aria-label={
              isArabic
                ? "الرئيسية - مؤسسة النعمان"
                : "Al Noman - Home"
            }
            className="
              flex shrink-0 items-center
              rounded-lg
              focus-visible:outline-none
              focus-visible:ring-2
              focus-visible:ring-skin-accent
            "
          >
            {logoError ? (
              <span className="h6 text-skin-heading">
                {isArabic
                  ? "مؤسسة النعمان"
                  : "Al Noman"}
              </span>
            ) : (
              <img
                src={
                  currentMode === "Dark"
                    ? darkLogo
                    : logo
                }
                alt={
                  isArabic
                    ? "مؤسسة النعمان للمحاماة"
                    : "Al Noman Law Office"
                }
                onError={() => setLogoError(true)}
                className="
                  block h-[48px]
                  w-[108px]
                  object-contain
                  sm:h-[52px]
                  sm:w-[165px]
                  xl:w-[185px]
                "
              />
            )}
          </Link>

          {/* ==================================
              DESKTOP NAVBAR
          ================================== */}

          <Navebar
            active={active}
            DropActive={DropActive}
            language={language}
          />

          {/* ==================================
              HEADER ACTIONS
          ================================== */}

          <div
            className="
              flex shrink-0
              items-center justify-end
              gap-1.5
              sm:gap-2
            "
          >
            {/* Theme toggle */}
            <ToggleDark />

            {/* Language toggle */}
            <ToggleLanguages />

            {/* User / Dashboard */}
            <Link
              to={accountPath}
              className="
                group hidden h-10
                items-center justify-center
                gap-2 rounded-xl
                border border-skin-border
                bg-skin-soft/70
                px-4
                text-skin-heading
                transition-all duration-300

                hover:border-skin-accent/35
                hover:bg-skin-raised

                focus-visible:outline-none
                focus-visible:ring-2
                focus-visible:ring-skin-accent

                dark:border-white/10
                dark:bg-white/[0.07]
                dark:text-white
                dark:hover:bg-white/[0.12]

                xl:inline-flex
              "
            >
        
<AccountIcon
  size={19}
  aria-hidden="true"
  className={
    !isAuth
      ? "directional-arrow text-skin-accent"
      : "text-skin-accent"
  }
/>


              <span className="button-sm whitespace-nowrap">
                {accountLabel}
              </span>
            </Link>

            {/* Mobile navigation button */}
            <button
              type="button"
              onClick={() =>
                setMenuOpen((previous) => !previous)
              }
              aria-expanded={menuOpen}
              aria-controls="mobile-site-navigation"
              aria-label={
                menuOpen
                  ? isArabic
                    ? "إغلاق القائمة"
                    : "Close menu"
                  : isArabic
                  ? "فتح القائمة"
                  : "Open menu"
              }
              className="
                flex h-10 w-10
                shrink-0 items-center
                justify-center rounded-xl
                border border-skin-border
                bg-skin-soft/70
                text-skin-heading
                transition-all duration-300

                hover:border-skin-accent/35
                hover:bg-skin-raised

                focus-visible:outline-none
                focus-visible:ring-2
                focus-visible:ring-skin-accent

                dark:border-white/10
                dark:bg-white/[0.07]
                dark:text-white
                dark:hover:bg-white/[0.12]

                xl:hidden
              "
            >
              {menuOpen ? (
                <HiOutlineXMark
                  size={24}
                  aria-hidden="true"
                />
              ) : (
                <HiOutlineBars3
                  size={24}
                  aria-hidden="true"
                />
              )}
            </button>
          </div>
        </div>
      </header>

      {/* ======================================
          MOBILE MENU
      ====================================== */}

      {menuOpen && (
        <Navmobile
          active={active}
          DropActive={DropActive}
          language={language}
          accountPath={accountPath}
          accountLabel={accountLabel}
          AccountIcon={AccountIcon}
          onClose={() => setMenuOpen(false)}
        />
      )}

      {/* Space reserved for the fixed header */}
      <div
        aria-hidden="true"
        className="h-[76px] w-full shrink-0"
      />
    </>
  );
}

export default Header;
